/**
 * Security — top-level view, founder-approved 2026-10-20 (options A + D).
 *
 * Five sub-tabs, each selected by `?section=`:
 *   - summary    SecuritySummary — the overview that composes the four
 *                threats / blocking / protected-paths / campaigns reads
 *                in parallel and renders the "what happened" view.
 *   - attackers  AttackersView — a dedicated sender list with its own
 *                search, Addresses · Networks switch and Got-through
 *                filter. No Activity header, no Activity chrome. The
 *                `?threat=` deep link still opens the matching sender.
 *   - paths      PathsView — leads with the Path · Kind · Hits ·
 *                Status table; editors + categories are tucked behind
 *                "Edit path list". `section=paths` stays the URL id.
 *   - blocking   Blocking — reuses Settings → Blocking's addresses
 *                tab (SettingsInstallBlocking) untouched.
 *   - trusted    TrustedView (founder 2026-10-20) — add / remove
 *                trusted addresses (never blocked, never alerted on).
 *                Moved out of Blocking so admin pages (wp-admin,
 *                cPanel) have an obvious place to stay reachable.
 *
 * The sub-tab is held in `?section=`; a plain `/?view=security` opens
 * on Summary.
 */

import { useCallback, useEffect, useState } from 'react';

import type { SetView } from '../../app/view-router.js';
import { useSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { NumLabel } from '../../components/atoms/index.js';
import { SettingsInstallBlocking } from '../settings/SettingsInstallBlocking.js';
import { SecuritySummary } from './SecuritySummary.js';
import { AttackersView } from './AttackersView.js';
import { PathsView } from './PathsView.js';
import { TrustedView } from './TrustedView.js';

const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";
const monoFont = "'JetBrains Mono', monospace";

export type SecuritySection = 'summary' | 'attackers' | 'paths' | 'blocking' | 'trusted';

const SECTIONS: readonly { id: SecuritySection; label: string }[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'attackers', label: 'Attackers' },
  // Renamed from "Attack paths" → "Paths" (founder 2026-10-20).
  // `section=paths` stays the stable URL id so bookmarks don't move.
  { id: 'paths', label: 'Paths' },
  { id: 'blocking', label: 'Blocking' },
  { id: 'trusted', label: 'Trusted' },
];

function readSection(): SecuritySection {
  if (typeof window === 'undefined') return 'summary';
  try {
    const v = new URL(window.location.href).searchParams.get('section');
    if (v === 'attackers' || v === 'paths' || v === 'blocking' || v === 'summary' || v === 'trusted') return v;
  } catch { /* fall through */ }
  return 'summary';
}

function writeSection(s: SecuritySection): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('section', s);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch { /* URL sync is enhancement only */ }
}

export interface SecurityProps {
  readonly setView: SetView;
}

export function Security({ setView }: SecurityProps): JSX.Element | null {
  const C = useC();
  const { activeSite } = useSites();
  const [section, setSection] = useState<SecuritySection>(readSection);

  const choose = useCallback((s: SecuritySection): void => {
    setSection(s);
    writeSection(s);
  }, []);

  // Founder bug 2026-10-20: a sender opened from the Summary landed back
  // on the Summary. Put the sender on the URL (`?threat=`, which Attackers
  // reads on mount) and switch the tab here.
  const openSender = useCallback((sender_ref: string): void => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('threat', sender_ref);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch { /* best effort */ }
    choose('attackers');
    window.scrollTo({ top: 0 });
  }, [choose]);

  // Back / Forward: re-read the section when the URL changes under us.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = (): void => setSection(readSection());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (activeSite === null) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="mb-6">
        <NumLabel n={1}>{activeSite.domain} · security</NumLabel>
        <h1
          style={{
            fontFamily: serifFont,
            fontSize: 'clamp(36px, 6vw, 52px)',
            lineHeight: 1,
            color: C.text,
            letterSpacing: -1.5,
          }}
        >
          What happened on your <em>site</em>.
        </h1>
      </div>

      {/* Tab bar — never wraps; scrolls horizontally on narrow screens. */}
      <div
        role="tablist"
        aria-label="Security sections"
        data-testid="security-tabs"
        className="flex items-center gap-1"
        style={{
          borderBottom: `1px solid ${C.borderSoft}`,
          marginBottom: 20,
          overflowX: 'auto',
          overflowY: 'hidden',
          flexWrap: 'nowrap',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        {SECTIONS.map((s) => {
          const sel = section === s.id;
          return (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={sel}
              data-testid={`security-tab-${s.id}`}
              onClick={() => choose(s.id)}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${sel ? C.text : 'transparent'}`,
                color: sel ? C.text : C.muted,
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: sel ? 600 : 400,
                padding: '8px 14px',
                marginBottom: -1,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {section === 'summary' ? (
        <SecuritySummary site={activeSite} setView={setView} onSelectSection={choose} onOpenSender={openSender} />
      ) : section === 'attackers' ? (
        <AttackersView site={activeSite} onOpenBlocking={choose} />
      ) : section === 'paths' ? (
        <PathsView site={activeSite} />
      ) : section === 'trusted' ? (
        <TrustedView site={activeSite} />
      ) : (
        <SettingsInstallBlocking
          site={activeSite}
          onOpenTrusted={() => choose('trusted')}
          onOpenSender={(block) => {
            // Founder bug 2026-10-20 (Report 6): resolve the row to a
            // sender_ref. If the blocklist entry remembers which alert
            // created it, open that sender group directly; otherwise
            // seed the Attackers search with the address so the row
            // becomes findable without inventing an id.
            if (block.source_threat_alert_id !== null) {
              openSender(block.source_threat_alert_id);
              return;
            }
            try {
              const url = new URL(window.location.href);
              url.searchParams.delete('threat');
              url.searchParams.set('q', block.target_display);
              window.history.replaceState(window.history.state, '', url.toString());
            } catch { /* best effort */ }
            choose('attackers');
            window.scrollTo({ top: 0 });
          }}
        />
      )}

      {/* Kick marker reserved for mono headers nested below the shell. */}
      <span data-mono-shell={monoFont} style={{ display: 'none' }} aria-hidden="true" />
    </div>
  );
}
